import axios from 'axios';

// Determine the API base URL dynamically
const getBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Otherwise, derive it from the current host
  const { protocol, hostname, port } = window.location;

  // If we're running on localhost with a specific port for frontend
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For local development, the backend is typically running on port 8040
    return 'http://localhost:8040';
  }

  // For production, use the same origin since the FastAPI backend serves the frontend
  // The backend handles API routes with the /api prefix
  return window.location.origin;
};

// Create an axios instance with default config
const api = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to include the token in all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Clear token from localStorage
      localStorage.removeItem('token');

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const documentService = {
  // Get all documents
  getDocuments: async () => {
    const response = await api.get('/api/documents/list');
    return response.data;
  },

  // Add a document
  addDocument: async (documentData) => {
    const response = await api.post('/api/documents', documentData);
    return response.data;
  },

  // Update a document
  updateDocument: async (documentId, documentData) => {
    const response = await api.put(`/api/documents/${documentId}`, documentData);
    return response.data;
  },

  // Delete a document
  deleteDocument: async (documentId) => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  }
};

// Media Service
export const mediaService = {
  // Get all media items
  getAllMedia: async (skip = 0, limit = 100, search = null, mediaType = null, dateFrom = null, dateTo = null) => {
    let params = { skip, limit };
    if (search) {
      params.search = search;
    }
    if (mediaType) {
      params.media_type = mediaType;
    }
    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }
    const response = await api.get('/api/media', { params });
    return response.data;
  }
};

// QA Knowledge Base Service
export const qaService = {
  // Get all QA items
  getQAItems: async (skip = 0, limit = 300, isActive = null, search = null) => {
    let params = { skip, limit };
    if (isActive !== null) {
      params.is_active = isActive;
    }
    if (search) {
      params.search = search;
    }
    const response = await api.get('/api/qa', { params });
    return response.data;
  },

  // Search QA items
  searchQAItems: async (search, skip = 0, limit = 300, isActive = null) => {
    let params = { skip, limit, search };
    if (isActive !== null) {
      params.is_active = isActive;
    }
    const response = await api.get('/api/qa', { params });
    return response.data;
  },

  // Get QA item by ID
  getQAItemById: async (qaId) => {
    const response = await api.get(`/api/qa/${qaId}`);
    return response.data;
  },

  // Create QA item
  createQAItem: async (qaItem) => {
    const response = await api.post('/api/qa', qaItem);
    return response.data;
  },

  // Update QA item
  updateQAItem: async (qaId, qaUpdate) => {
    const response = await api.put(`/api/qa/${qaId}`, qaUpdate);
    return response.data;
  },

  // Delete QA item
  deleteQAItem: async (qaId) => {
    const response = await api.delete(`/api/qa/${qaId}`);
    return response.data;
  },

  // Import QA items
  importQAItems: async (qaItems, attachRule = 'always') => {
    const response = await api.post('/api/qa/import', qaItems, {
      params: { attach_rule: attachRule }
    });
    return response.data;
  },

  // QA Tags
  getQATags: async () => {
    const response = await api.get('/api/qa-tags');
    return response.data;
  },

  // Create QA tag
  createQATag: async (tag) => {
    const response = await api.post('/api/qa-tags', tag);
    return response.data;
  },

  // Update QA tag
  updateQATag: async (tagId, tag) => {
    const response = await api.put(`/api/qa-tags/${tagId}`, tag);
    return response.data;
  },

  // Delete QA tag
  deleteQATag: async (tagId) => {
    const response = await api.delete(`/api/qa-tags/${tagId}`);
    return response.data;
  },

  // Export QA items as simplified JSON
  exportQAItems: async ({ tagId = null, isActive = true } = {}) => {
    try {
      const params = {};
      if (tagId) params.tag_id = tagId;
      if (typeof isActive === 'boolean') params.is_active = isActive;
      const response = await api.get('/api/qa/export', {
        params,
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting QA items:', error);
      throw error;
    }
  },

  // Export QA items as HTML table
  exportQAItemsToHTML: async ({ tagId = null, isActive = true } = {}) => {
    try {
      const params = {};
      if (tagId) params.tag_id = tagId;
      if (typeof isActive === 'boolean') params.is_active = isActive;
      const response = await api.get('/api/qa/export/html', {
        params,
        responseType: 'blob', // Important for downloading files
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting QA items to HTML:', error);
      throw error;
    }
  },
};

export const embeddedSignupService = {
  exchangeAuthorizationCode: async (code) => {
    // Log the current page URL as potential redirect_uri reference (for debugging)
    const currentRedirectUri = window.location.origin + window.location.pathname;
    console.info('EmbeddedSignup: Sending code exchange request', {
      redirectUri: currentRedirectUri,
      codeLength: code?.length || 0,
    });

    const response = await api.post('/api/meta-whatsapp-onboarding/oauth/exchange', { code });
    return response.data;
  },

  saveOnboardingRecord: async ({ waba_id, phone_number_id, access_token, expires_in }) => {
    const response = await api.post('/api/meta-whatsapp-onboarding/onboarding/save', {
      waba_id,
      phone_number_id,
      access_token,
      expires_in,
    });
    return response.data;
  },
};

export const onboardingService = {
  getByToken: async (token) => {
    const response = await api.get('/api/onboarding', {
      params: { token },
    });
    return response.data;
  },

  updateStep: async ({ token, current_step }) => {
    const response = await api.post('/api/onboarding/step', {
      token,
      current_step,
    });
    return response.data;
  },

  createAdmin: async ({ token, email, password }) => {
    const response = await api.post('/api/onboarding/admin', {
      token,
      email,
      password,
    });
    return response.data;
  },

  saveAnswers: async ({ token, answers }) => {
    const response = await api.post('/api/onboarding/answers', {
      token,
      answers,
    });
    return response.data;
  },

  exchangeAuthorizationCode: async ({ token, code, redirect_uri }) => {
    const response = await api.post('/api/onboarding/embedded-signup/oauth/exchange', {
      token,
      code,
      redirect_uri,
    });
    return response.data;
  },

  saveEmbeddedSignup: async ({ token, waba_id, phone_number_id, access_token, expires_in }) => {
    const response = await api.post('/api/onboarding/embedded-signup/save', {
      token,
      waba_id,
      phone_number_id,
      access_token,
      expires_in,
    });
    return response.data;
  },
};

// API functions for authentication
export const authService = {
  // Login user
  login: async (username, password, clid = "CLID0000") => {
    try {
      const formData = new FormData();
      formData.append('username', username);
      formData.append('password', password);
      formData.append('client_id', clid);

      const response = await api.post('/api/auth/token', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Register user
  register: async (username, email, password, clid = "CLID0000") => {
    try {
      const response = await api.post('/api/auth/register', {
        username,
        email,
        password,
        clid
      });
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser: async () => {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch (error) {
      console.error('Error getting current user:', error);
      throw error;
    }
  },

  // Phone login - Step 1: Query clinics by phone number
  phoneLogin: async (phoneNumber) => {
    try {
      const response = await api.post('/api/auth/phone-login', {
        phone_number: phoneNumber
      });
      return response.data;
    } catch (error) {
      console.error('Phone login error:', error);
      throw error;
    }
  },

  // Phone login - Step 2: Send verification code
  sendVerification: async (phoneNumber, clinicId) => {
    try {
      const response = await api.post('/api/auth/send-verification', {
        phone_number: phoneNumber,
        clinic_id: clinicId
      });
      return response.data;
    } catch (error) {
      console.error('Send verification error:', error);
      throw error;
    }
  },

  // Phone login - Step 3: Verify code and login
  verifyCode: async (phoneNumber, clinicId, verificationCode) => {
    try {
      const response = await api.post('/api/auth/verify-code', {
        phone_number: phoneNumber,
        clinic_id: clinicId,
        verification_code: verificationCode
      });
      return response.data;
    } catch (error) {
      console.error('Verify code error:', error);
      throw error;
    }
  },

  // Update username
  updateUsername: async (newUsername, currentPassword) => {
    try {
      const response = await api.put('/api/auth/update-username', {
        new_username: newUsername,
        current_password: currentPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update username error:', error);
      throw error;
    }
  },

  // Update password
  updatePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put('/api/auth/update-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  },

  // Update username with 2FA (for temp users)
  updateUsernameWith2FA: async (newUsername) => {
    try {
      const response = await api.post('/api/auth/update-username-2fa', {
        new_username: newUsername
      });
      return response.data;
    } catch (error) {
      console.error('Update username 2FA error:', error);
      throw error;
    }
  },

  // Verify username update with code
  verifyUsernameUpdate: async (newUsername, verificationCode) => {
    try {
      const response = await api.post('/api/auth/verify-username-update', {
        new_username: newUsername,
        verification_code: verificationCode
      });
      return response.data;
    } catch (error) {
      console.error('Verify username update error:', error);
      throw error;
    }
  },

  // Update password with 2FA (for temp users)
  updatePasswordWith2FA: async (newPassword) => {
    try {
      const response = await api.post('/api/auth/update-password-2fa', {
        new_password: newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Update password 2FA error:', error);
      throw error;
    }
  },

  // Verify password update with code
  verifyPasswordUpdate: async (newPassword, verificationCode) => {
    try {
      const response = await api.post('/api/auth/verify-password-update', {
        new_password: newPassword,
        verification_code: verificationCode
      });
      return response.data;
    } catch (error) {
      console.error('Verify password update error:', error);
      throw error;
    }
  },

  // Convert temp user to regular user
  convertTempUser: async (username, email, password) => {
    try {
      const response = await api.post('/api/auth/convert-temp-user', {
        username: username,
        email: email,
        password: password
      });
      return response.data;
    } catch (error) {
      console.error('Convert temp user error:', error);
      throw error;
    }
  },

  // Get clinic modules
  // Get available clinics for clinic swap
  getAvailableClinics: async () => {
    try {
      const response = await api.get('/api/auth/available-clinics');
      return response.data;
    } catch (error) {
      console.error('Get available clinics error:', error);
      throw error;
    }
  },

  // Swap clinic
  swapClinic: async (clinicId) => {
    try {
      const response = await api.post('/api/auth/swap-clinic', {
        clinic_id: clinicId
      });
      return response.data;
    } catch (error) {
      console.error('Swap clinic error:', error);
      throw error;
    }
  },

  getClinicModules: async () => {
    try {
      const response = await api.get('/api/auth/clinic-modules');
      return response.data;
    } catch (error) {
      console.error('Error getting clinic modules:', error);
      throw error;
    }
  },
};

// API functions for recordings
export const recordingService = {
  // Get all recordings with pagination and filtering
  getRecordings: async (page = 1, search = null, status = null) => {
    try {
      const params = { page };
      if (search) params.search = search;
      if (status) params.status = status;

      const response = await api.get('/recordings/', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching recordings:', error);
      throw error;
    }
  },

  // Get recording details by ID
  getRecordingDetails: async (recordingId) => {
    try {
      const response = await api.get(`/recordings/${recordingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching recording ${recordingId}:`, error);
      throw error;
    }
  },

  // Download recording
  downloadRecording: async (recordingId) => {
    try {
      const response = await api.get(`/recordings/${recordingId}/download`);
      return response.data;
    } catch (error) {
      console.error(`Error downloading recording ${recordingId}:`, error);
      throw error;
    }
  },

  // Get recording transcript
  getRecordingTranscript: async (recordingId) => {
    try {
      const response = await api.get(`/recordings/${recordingId}/transcript`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching transcript for recording ${recordingId}:`, error);
      throw error;
    }
  },

  // Update recording transcript
  updateRecordingTranscript: async (recordingId, transcriptData) => {
    try {
      const response = await api.put(`/recordings/${recordingId}/transcript`, transcriptData);
      return response.data;
    } catch (error) {
      console.error(`Error updating transcript for recording ${recordingId}:`, error);
      throw error;
    }
  }
};

// API functions for calendar
export const calendarService = {
  // Get all appointments
  getAppointments: async (startDate, endDate) => {
    try {
      const response = await api.get(`/api/appointments/list`, {
        params: {
          start_date: startDate,
          end_date: endDate
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching appointments:', error);
      throw error;
    }
  },

  // Create a new appointment
  createAppointment: async (appointmentData) => {
    try {
      const response = await api.post('/api/appointments', appointmentData);
      return response.data;
    } catch (error) {
      console.error('Error creating appointment:', error);
      throw error;
    }
  },

  // Get doctors list
  getDoctors: async () => {
    try {
      const response = await api.get('/api/doctors/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching doctors:', error);
      throw error;
    }
  },

  // Update an appointment
  updateAppointment: async (appointmentId, appointmentData) => {
    try {
      const response = await api.put(`/api/appointments/${appointmentId}`, appointmentData);
      return response.data;
    } catch (error) {
      console.error(`Error updating appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  // Update an appointment's status
  updateAppointmentStatus: async (appointmentId, status) => {
    try {
      const response = await api.put(`/api/appointments/${appointmentId}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating appointment status ${appointmentId}:`, error);
      throw error;
    }
  },

  // Delete an appointment
  deleteAppointment: async (appointmentId) => {
    try {
      const response = await api.delete(`/api/appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  // Get appointment details
  getAppointmentDetails: async (appointmentId) => {
    try {
      const response = await api.get(`/api/appointments/${appointmentId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointment ${appointmentId}:`, error);
      throw error;
    }
  },

  // Get today's appointments
  getTodayAppointments: async () => {
    try {
      const response = await api.get('/api/appointments/today');
      return response.data;
    } catch (error) {
      console.error('Error fetching today\'s appointments:', error);
      throw error;
    }
  },

  // Get upcoming appointments (next 7 days)
  getUpcomingAppointments: async () => {
    try {
      const response = await api.get('/api/appointments/upcoming');
      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming appointments:', error);
      throw error;
    }
  },

  // Schedule-related API functions

  // Export all doctor schedules for the current clinic
  exportDoctorSchedulesJson: async () => {
    const response = await api.get('/api/schedules/export');
    return response.data;
  },

  // Import/configure all doctor schedules from JSON
  importDoctorSchedulesJson: async (payload) => {
    const response = await api.post('/api/schedules/import', payload);
    return response.data;
  },

  // Get active schedules for a specific doctor (for operational use)
  getDoctorSchedules: async (doctorId) => {
    try {
      const response = await api.get(`/api/schedules/doctor/${doctorId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedules for doctor ${doctorId}:`, error);
      throw error;
    }
  },

  // Get all schedules for a specific doctor (including inactive ones for management)
  getAllDoctorSchedules: async (doctorId) => {
    try {
      const response = await api.get(`/api/schedules/doctor/${doctorId}/all`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching all schedules for doctor ${doctorId}:`, error);
      throw error;
    }
  },

  // Get schedules for all doctors for a specific date
  getSchedulesByDate: async (date) => {
    try {
      const response = await api.get(`/api/schedules/date/${date}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedules for date ${date}:`, error);
      throw error;
    }
  },

  // Get schedule for a specific doctor on a specific date
  getDoctorScheduleByDate: async (doctorId, date) => {
    try {
      const response = await api.get(`/api/schedules/doctor/${doctorId}/date/${date}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching schedule for doctor ${doctorId} on date ${date}:`, error);
      throw error;
    }
  },

  // Create a new schedule
  createSchedule: async (scheduleData) => {
    try {
      const response = await api.post('/api/schedules/create', scheduleData);
      return response.data;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw error;
    }
  },

  // Update an existing schedule
  updateSchedule: async (scheduleId, scheduleData) => {
    try {
      const response = await api.put(`/api/schedules/${scheduleId}`, scheduleData);
      return response.data;
    } catch (error) {
      console.error(`Error updating schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  // Delete (deactivate) a schedule
  deleteSchedule: async (scheduleId) => {
    try {
      const response = await api.delete(`/api/schedules/${scheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting schedule ${scheduleId}:`, error);
      throw error;
    }
  },

  // Block Schedule-related API functions

  // Get block schedules for a specific doctor
  getDoctorBlockSchedules: async (doctorId) => {
    try {
      const response = await api.get(`/api/block-schedules/doctor/${doctorId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching block schedules for doctor ${doctorId}:`, error);
      throw error;
    }
  },

  // Get all block schedules for the clinic
  getAllBlockSchedules: async () => {
    try {
      const response = await api.get('/api/block-schedules/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching all block schedules:', error);
      throw error;
    }
  },

  // Get block schedules for a specific date
  getBlockSchedulesByDate: async (date, doctorId = null) => {
    try {
      const params = {};
      if (doctorId) {
        params.doctor_id = doctorId;
      }
      const response = await api.get(`/api/block-schedules/date/${date}`, { params });
      return response.data;
    } catch (error) {
      console.error(`Error fetching block schedules for date ${date}:`, error);
      throw error;
    }
  },

  // Create a new block schedule
  createBlockSchedule: async (blockScheduleData) => {
    try {
      const response = await api.post('/api/block-schedules/create', blockScheduleData);
      return response.data;
    } catch (error) {
      console.error('Error creating block schedule:', error);
      throw error;
    }
  },

  // Update an existing block schedule
  updateBlockSchedule: async (blockScheduleId, blockScheduleData) => {
    try {
      const response = await api.put(`/api/block-schedules/${blockScheduleId}`, blockScheduleData);
      return response.data;
    } catch (error) {
      console.error(`Error updating block schedule ${blockScheduleId}:`, error);
      throw error;
    }
  },

  // Delete (deactivate) a block schedule
  deleteBlockSchedule: async (blockScheduleId) => {
    try {
      const response = await api.delete(`/api/block-schedules/${blockScheduleId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting block schedule ${blockScheduleId}:`, error);
      throw error;
    }
  },

  // Insert predefined holidays
  insertHolidays: async (doctorId = null) => {
    try {
      const params = {};
      if (doctorId) {
        params.doctor_id = doctorId;
      }
      const response = await api.post('/api/block-schedules/insert-holidays', {}, { params });
      return response.data;
    } catch (error) {
      console.error('Error inserting holidays:', error);
      throw error;
    }
  }
};

// API functions for dashboard
export const dashboardService = {
  // Get summary statistics
  getStats: async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Get recent activity
  getRecentActivity: async (limit = 5) => {
    try {
      const response = await api.get('/api/dashboard/activity', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  }
};

// API functions for doctor management
export const doctorService = {
  // Get all doctors
  getDoctors: async () => {
    try {
      const response = await api.get('/api/doctors/list');
      return response.data;
    } catch (error) {
      console.error('Error fetching doctors:', error);
      throw error;
    }
  },

  // Add a new doctor
  addDoctor: async (doctorData) => {
    try {
      const response = await api.post('/api/doctors', doctorData);
      return response.data;
    } catch (error) {
      console.error('Error adding doctor:', error);
      throw error;
    }
  },

  // Update an existing doctor
  updateDoctor: async (doctorId, doctorData) => {
    try {
      const response = await api.put(`/api/doctors/${doctorId}`, doctorData);
      return response.data;
    } catch (error) {
      console.error(`Error updating doctor ${doctorId}:`, error);
      throw error;
    }
  },

  // Delete a doctor
  deleteDoctor: async (doctorId) => {
    try {
      const response = await api.delete(`/api/doctors/${doctorId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting doctor ${doctorId}:`, error);
      throw error;
    }
  }
};

// API functions for location management
export const locationService = {
  // Get all locations
  getLocations: async (skip = 0, limit = 100, search = null) => {
    try {
      const params = { skip, limit };
      if (search) params.search = search;
      const response = await api.get('/api/locations/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  // Add a new location
  addLocation: async (locationData) => {
    try {
      const response = await api.post('/api/locations', locationData);
      return response.data;
    } catch (error) {
      console.error('Error adding location:', error);
      throw error;
    }
  },

  // Update an existing location
  updateLocation: async (locationId, locationData) => {
    try {
      const response = await api.put(`/api/locations/${locationId}`, locationData);
      return response.data;
    } catch (error) {
      console.error(`Error updating location ${locationId}:`, error);
      throw error;
    }
  },

  // Delete a location
  deleteLocation: async (locationId) => {
    try {
      const response = await api.delete(`/api/locations/${locationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting location ${locationId}:`, error);
      throw error;
    }
  }
};

// API functions for file operations
export const fileService = {
  // Import Excel file
  importExcelFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/api/file/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error importing Excel file:', error);
      throw error;
    }
  },

  // Process imported data
  processImportedData: async (actionType, data) => {
    try {
      const response = await api.post('/api/file/import/action', {
        action_type: actionType,
        data: data
      });
      return response.data;
    } catch (error) {
      console.error('Error processing imported data:', error);
      throw error;
    }
  }
};

// API functions for TODO setup
export const todoSetupService = {
  // Get TODO setup status
  getTodoSetupStatus: async () => {
    try {
      const response = await api.get('/api/todo-setup');
      return response.data;
    } catch (error) {
      console.error('Error fetching TODO setup status:', error);
      throw error;
    }
  }
};

// API functions for clinics
export const clinicService = {
  // Get conversations for a clinic
  getClinicConversations: async (clinicId, page = 1, perPage = 10, search = '') => {
    try {
      const response = await api.get(`/api/conversations/clinic/${clinicId}`, {
        params: { page, per_page: perPage, search: search || undefined },
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching conversations for clinic ${clinicId}:`, error);
      throw error;
    }
  },

  // Get messages for a conversation
  getConversationMessages: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get earlier messages for a conversation before a specific message ID
  getConversationMessagesBefore: async (conversationId, cursor) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/messages`, {
        params: { before_id: cursor, limit: 60 }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching earlier messages for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get appointments for a conversation
  getConversationAppointments: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/appointments`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointments for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Get events for a conversation
  getConversationEvents: async (conversationId) => {
    try {
      const response = await api.get(`/api/conversations/${conversationId}/events`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching events for conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Notify clinic by human
  notifyClinicByHuman: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/notify_clinic`, {
        message: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error notifying clinic for conversation ${conversationId}:`, error);
      throw error;
    }
  }
};

// Get nexi global template
export const clinic_template = {
  search: async (clinicId) => {
    try {
      const response = await api.get(`/api/template/${encodeURIComponent(clinicId)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching nexi global templates (clinicId=${clinicId}):`, error);
      throw error;
    }
  },

  searchGlobal: async (templateId) => {
    try {
      const response = await api.get(`/api/template/global/${encodeURIComponent(templateId)}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching global-only template (templateId=${templateId}):`, error);
      throw error;
    }
  },

  update: async (clinicId, templateId, body) => {
    try {
      const response = await api.put(
        `/api/template/${encodeURIComponent(clinicId)}/${encodeURIComponent(templateId)}`,
        body
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error updating clinic template (clinicId=${clinicId}, templateId=${templateId}):`,
        error
      );
      throw error;
    }
  },
};

// API functions for conversations
export const conversationService = {
  // Send a message in a conversation
  sendMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Send a message via outbound handler
  sendOutboundMessage: async (conversationId, message) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_message`, {
        message_body: message
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending outbound message in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Send a template message
  sendTemplate: async (conversationId, templateId) => {
    try {
      const response = await api.post(`/api/conversations/${conversationId}/send_message`, {
        template_name: templateId
      });
      return response.data;
    } catch (error) {
      console.error(`Error sending template in conversation ${conversationId}:`, error);
      throw error;
    }
  },

  // Set event metadata
  setEventMetadata: async (eventId, key, value) => {
    try {
      const response = await api.post('/api/conversation-activities/set-metadata', {
        event_id: eventId,
        key: key,
        value: value
      });
      return response.data;
    } catch (error) {
      console.error(`Error setting metadata for event ${eventId}:`, error);
      throw error;
    }
  }
};

// API functions for patients
export const patientService = {
  // Get patient by conversation ID
  getPatientByConversationId: async (conversationId) => {
    try {
      const response = await api.get(`/api/patients/by-conversation/${conversationId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching patient by conversation ID ${conversationId}:`, error);
      throw error;
    }
  },

  // Get patient details by ID
  getPatient: async (patientId) => {
    try {
      const response = await api.get(`/api/patients/${patientId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching patient ${patientId}:`, error);
      throw error;
    }
  },

  // Get patient appointments
  getPatientAppointments: async (patientId) => {
    try {
      const response = await api.get(`/api/patients/${patientId}/appointments`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointments for patient ${patientId}:`, error);
      throw error;
    }
  },

  // Get patient media
  getPatientMedia: async (patientId) => {
    try {
      const response = await api.get(`/api/patients/${patientId}/media`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching media for patient ${patientId}:`, error);
      throw error;
    }
  }
};

// API functions for templates
export const templateService = {
  // Get available templates
  getTemplates: async () => {
    return {
      templates: [
        { id: 'hello_world', content: 'Welcome and congratulations!! This message demonstrates your ability to send a WhatsApp message notification from the Cloud API, hosted by Meta. Thank you for taking the time to test with us.' },
        { id: 'Appointment Confirmation', content: 'Your appointment has been confirmed for {date} at {time}.' },
        { id: 'Appointment Reminder', content: 'Reminder: You have an appointment scheduled for {date} at {time}.' },
        { id: 'General Information', content: 'Thank you for contacting us. Our office hours are Monday-Friday, 9am-5pm.' },
        { id: 'Follow-up', content: 'Following up on your recent visit. How are you feeling?' }
      ]
    };
  },

  // Get template by CLID and template name
  getTemplateByClidAndName: async (clid, templateName) => {
    try {
      const response = await api.get('/api/templates/by-clid-and-name', {
        params: { clid, template_name: templateName }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template by CLID and name:', error);
      throw error;
    }
  },

  // Get template message preview with variables substituted
  getTemplatePreview: async (clid, templateName, contentVariables) => {
    try {
      const response = await api.post('/api/templates/preview', {
        clid,
        template_name: templateName,
        content_variables: contentVariables
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template preview:', error);
      throw error;
    }
  },

  // Meta WhatsApp Template methods
  // Create a new Meta WhatsApp template
  createMetaTemplate: async (templateData) => {
    try {
      const response = await api.post('/api/templates/meta/create', templateData);
      return response.data;
    } catch (error) {
      console.error('Error creating Meta template:', error);
      throw error;
    }
  },

  // List all Meta WhatsApp templates
  listMetaTemplates: async () => {
    try {
      const response = await api.get('/api/templates/meta/list');
      return response.data;
    } catch (error) {
      console.error('Error listing Meta templates:', error);
      throw error;
    }
  },

  // Refresh Meta WhatsApp templates
  refreshMetaTemplates: async () => {
    try {
      const response = await api.post('/api/templates/meta/refresh');
      return response.data;
    } catch (error) {
      console.error('Error listing Meta templates:', error);
      throw error;
    }
  },

  // Get a single Meta WhatsApp template by ID
  getMetaTemplate: async (templateId) => {
    try {
      const response = await api.get(`/api/templates/meta/${templateId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching Meta template:', error);
      throw error;
    }
  },

  // Download CSV template for a specific template
  downloadTemplateCSV: async (templateId) => {
    try {
      const response = await api.get(`/api/templates/meta/${templateId}/csv-template`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading CSV template:', error);
      throw error;
    }
  },

  // Upload CSV and send broadcast messages
  uploadBroadcastCSV: async (templateId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/api/templates/meta/broadcast?template_id=${templateId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading broadcast CSV:', error);
      throw error;
    }
  },

  // Resend a template to Meta for review
  resendMetaTemplate: async (templateId) => {
    try {
      const response = await api.post(`/api/templates/meta/${templateId}/resend`);
      return response.data;
    } catch (error) {
      console.error('Error resending Meta template:', error);
      throw error;
    }
  },
};

// API functions for appointment reminder jobs
export const appointmentReminderJobService = {
  // Get all appointment reminder jobs
  getAppointmentReminderJobs: async (skip = 0, limit = 100, search = null, isActive = null, jobStatus = null, version = null) => {
    try {
      let params = { skip, limit };
      if (search) {
        params.search = search;
      }
      if (isActive !== null) {
        params.is_active = isActive;
      }
      if (jobStatus) {
        params.job_status = jobStatus;
      }
      if (version) {
        params.version = version;
      }
      const response = await api.get('/api/appointment-reminder-jobs/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment reminder jobs:', error);
      throw error;
    }
  },

  // Get appointment reminder job entries for a specific job
  getAppointmentReminderJobEntries: async (jobId, skip = 0, limit = 100, search = null, sendStatus = null) => {
    try {
      let params = { skip, limit };
      if (search) {
        params.search = search;
      }
      if (sendStatus) {
        params.send_status = sendStatus;
      }
      const response = await api.get(`/api/appointment-reminder-jobs/${jobId}/entries`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment reminder job entries:', error);
      throw error;
    }
  },

  // Upsert appointment reminder job entry
  upsertAppointmentReminderJobEntry: async (entryId, jobId, clid, entryData) => {
    try {
      const response = await api.post('/api/appointment-reminder-jobs/upsert-entry', {
        entry_id: entryId,
        job_id: jobId,
        clid: clid,
        entry: entryData
      });
      return response.data;
    } catch (error) {
      console.error('Error upserting appointment reminder job entry:', error);
      throw error;
    }
  },

  // Delete appointment reminder job entry
  deleteAppointmentReminderJobEntry: async (entryId) => {
    try {
      const response = await api.delete(`/api/appointment-reminder-jobs/entries/${entryId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting appointment reminder job entry:', error);
      throw error;
    }
  },

  // Delete appointment reminder job
  deleteAppointmentReminderJob: async (jobId) => {
    try {
      const response = await api.delete(`/api/appointment-reminder-jobs/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting appointment reminder job:', error);
      throw error;
    }
  },

  // Send appointment reminder job
  sendAppointmentReminderJob: async (jobId) => {
    try {
      const response = await api.post(`/api/appointment-reminder-jobs/${jobId}/send`);
      return response.data;
    } catch (error) {
      console.error('Error sending appointment reminder job:', error);
      throw error;
    }
  },

  // Reset appointment reminder job
  resetAppointmentReminderJob: async (jobId) => {
    try {
      const response = await api.post(`/api/appointment-reminder-jobs/${jobId}/reset`);
      return response.data;
    } catch (error) {
      console.error('Error resetting appointment reminder job:', error);
      throw error;
    }
  }
};

// API functions for appointment reminder settings
export const appointmentReminderSettingsService = {
  // Get all appointment reminder settings
  getAppointmentReminderSettings: async (filters = {}) => {
    try {
      const params = {
        skip: filters.skip || 0,
        limit: filters.limit || 100
      };
      if (filters.location_id !== undefined) params.location_id = filters.location_id;
      if (filters.doctor_id !== undefined) params.doctor_id = filters.doctor_id;
      if (filters.enabled !== undefined) params.enabled = filters.enabled;

      const response = await api.get('/api/appointment-reminder-settings/list', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching appointment reminder settings:', error);
      throw error;
    }
  },

  // Get a specific appointment reminder setting
  getAppointmentReminderSetting: async (settingId) => {
    try {
      const response = await api.get(`/api/appointment-reminder-settings/${settingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching appointment reminder setting ${settingId}:`, error);
      throw error;
    }
  },

  // Create a new appointment reminder setting
  createAppointmentReminderSetting: async (settingData) => {
    try {
      const response = await api.post('/api/appointment-reminder-settings', settingData);
      return response.data;
    } catch (error) {
      console.error('Error creating appointment reminder setting:', error);
      throw error;
    }
  },

  // Update an existing appointment reminder setting
  updateAppointmentReminderSetting: async (settingId, settingData) => {
    try {
      const response = await api.put(`/api/appointment-reminder-settings/${settingId}`, settingData);
      return response.data;
    } catch (error) {
      console.error(`Error updating appointment reminder setting ${settingId}:`, error);
      throw error;
    }
  },

  // Delete an appointment reminder setting
  deleteAppointmentReminderSetting: async (settingId) => {
    try {
      const response = await api.delete(`/api/appointment-reminder-settings/${settingId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting appointment reminder setting ${settingId}:`, error);
      throw error;
    }
  },

  // Toggle enabled status of an appointment reminder setting
  toggleAppointmentReminderSettingEnabled: async (settingId) => {
    try {
      const response = await api.post(`/api/appointment-reminder-settings/${settingId}/toggle-enabled`);
      return response.data;
    } catch (error) {
      console.error(`Error toggling appointment reminder setting ${settingId}:`, error);
      throw error;
    }
  }
};

// API functions for subscriptions
export const subscriptionService = {
  // Get all subscriptions
  getSubscriptions: async (clid, limit = 10, offset = 0) => {
    try {
      const response = await api.get('/api/subscriptions', {
        params: { clid, limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      throw error;
    }
  },

  // Create a new subscription
  createSubscription: async (clid, subscriptionData) => {
    try {
      const response = await api.post('/api/subscriptions', subscriptionData, {
        params: { clid }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  },

  // Update an existing subscription
  updateSubscription: async (subscriptionId, clid, subscriptionData) => {
    try {
      const response = await api.put(`/api/subscriptions/${subscriptionId}`, subscriptionData, {
        params: { clid }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating subscription ${subscriptionId}:`, error);
      throw error;
    }
  },

  // Delete a subscription
  deleteSubscription: async (subscriptionId) => {
    try {
      const response = await api.delete(`/api/subscriptions/${subscriptionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting subscription ${subscriptionId}:`, error);
      throw error;
    }
  },

  // Get webhook settings for current clinic
  getWebhookSettings: async () => {
    try {
      const response = await api.get('/api/clinic/subscription-webhook/settings');
      return response.data;
    } catch (error) {
      console.error('Error fetching webhook settings:', error);
      throw error;
    }
  },

  // Upsert webhook settings for current clinic
  upsertWebhookSettings: async (settings) => {
    try {
      const response = await api.patch('/api/clinic/subscription-webhook/settings', settings);
      return response.data;
    } catch (error) {
      console.error('Error updating webhook settings:', error);
      throw error;
    }
  },

  // Trigger webhook delivery test for current clinic
  triggerWebhookTest: async (eventType, payload) => {
    try {
      const response = await api.post('/api/clinic/subscription-webhook/test-trigger', {
        event_type: eventType,
        payload: payload
      });
      return response.data;
    } catch (error) {
      console.error('Error triggering webhook test:', error);
      throw error;
    }
  }
};

// API functions for analytics
export const analyticsService = {
  // Lightweight summary for dashboard (does not call export-conversations)
  getSummary: async (params = {}) => {
    try {
      const response = await api.get('/api/analytics/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  },
  // Export conversations (used only from Analytics/ConversationExport and Analytics page)
  exportConversations: async (exportData) => {
    try {
      // Allow up to 2 minutes for wide date ranges; the backend handler is now
      // single-query but the JSON payload can still be sizeable on busy clinics.
      const response = await api.post('/api/analytics/export-conversations', exportData, {
        timeout: 120000,
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting conversations:', error);
      throw error;
    }
  }
};

// Export function for direct use
export const exportConversations = analyticsService.exportConversations;

// General API service for common HTTP operations
export const apiService = {
  get: async (url, config = {}) => {
    return api.get(url, config);
  },
  post: async (url, data = {}, config = {}) => {
    return api.post(url, data, config);
  },
  put: async (url, data = {}, config = {}) => {
    return api.put(url, data, config);
  },
  delete: async (url, config = {}) => {
    return api.delete(url, config);
  }
};

// API functions for toolbox
export const toolboxService = {
  // Call agent with content
  callAgent: async (content) => {
    try {
      const response = await api.post('/api/toolbox/ogc-chief-complaint-test', {
        content: content
      });
      return response.data;
    } catch (error) {
      console.error('Error calling toolbox agent:', error);
      // Return a structured error response to maintain consistent format
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Failed to call agent'
      };
    }
  }
};

export default api;
